import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  IconButton,
  Breadcrumbs,
  Link,
  TextField,
  Button,
  Skeleton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from "@mui/material";

import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { Snackbar, Alert } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import VisibilityIcon from "@mui/icons-material/Visibility";
import SearchIcon from "@mui/icons-material/Search";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import { useNavigate, useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";

/* ================= STYLE ================= */
const iconBtnStyle = {
  background: "#f5f5f5",
  borderRadius: 2,
  transition: "0.2s",
  "&:hover": {
    background: "#e0e0e0",
    transform: "scale(1.1)",
  },
};

const statusStyle = (active) => ({
  display: "inline-block",
  mt: 0.5,
  px: 1,
  py: 0.2,
  borderRadius: 10,
  fontSize: 11,
  background: active ? "#e8f5e9" : "#ffebee",
  color: active ? "#2e7d32" : "#c62828",
  fontWeight: 500,
});

/* ================= CARD ================= */
const RecipeCard = ({
  r,
  getName,
  navigate,
  categoryId,
  setDeleteDialogOpen,
  setRecipeToDelete,
  toggleRecipeActive,
  showSnack,canAdmin,userState,
}) => {
  return (
    <Box
      onClick={() =>
        navigate(`/Recipe/${r.id}`, {
          state: { categoryId }, // 🔥 AJOUT IMPORTANT
        })
      }
      sx={{
        display: "flex",
        alignItems: "center",
        background: "white",
        borderRadius: 3,
        overflow: "hidden",
        boxShadow: "0 4px 14px rgba(0,0,0,0.08)",
        cursor: "pointer",
        transition: "0.2s",
        "&:hover": {
          transform: "translateY(-3px)",
          boxShadow: "0 10px 25px rgba(0,0,0,0.12)",
        },
      }}
    >
      {/* IMAGE */}
      <Box
        sx={{
          width: 95,
          height: 95,
          flexShrink: 0,
          background: "#f5f5f5",
        }}
      >
        {r.imageUrl ? (
          <img
            src={r.imageUrl}
            alt=""
            style={{
              width: "100%",
              height: "100%",
              objectFit: "cover",
            }}
          />
        ) : (
          <Box
            sx={{
              width: "100%",
              height: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: 12,
              color: "#999",
            }}
          >
            No image
          </Box>
        )}
      </Box>

      {/* CONTENT */}
      <Box sx={{ flex: 1, p: 1.5 }}>
        <Typography fontWeight={600} fontSize={14}>
          {getName(r)}
        </Typography>

        <Typography fontSize={12} color="text.secondary">
          Recette #{r.id}
        </Typography>

        <Box sx={statusStyle(r.estActif)}>
          {r.estActif ? "Actif" : "Inactive"}
        </Box>
      </Box>

      {/* ACTIONS */}
      <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5, pr: 1 }}>
        <IconButton
          size="small"
          sx={{
            ...iconBtnStyle,
            color: r.estActif ? "#4caf50" : "#9e9e9e",
          }}
          onClick={async (e) => {
            e.stopPropagation();

            try {
              await toggleRecipeActive(r);
            } catch (err) {
              showSnack(err.message, "error");
            }
          }}
        >
          {r.estActif ? (
            <VisibilityIcon fontSize="small" />
          ) : (
            <VisibilityOffIcon fontSize="small" />
          )}
        </IconButton>
        <IconButton
  size="small"
  sx={iconBtnStyle}
  onClick={(e) => {
    e.stopPropagation();

    // ❌ Pas admin
    if (!canAdmin) {
      showSnack(
        userState.lang === "FR"
          ? "Vous n'avez pas les droits pour modifier cette recette"
          : "You are not authorized to edit this recipe",
        "warning"
      );
      return;
    }

    // ✅ Admin
    navigate(`/Recipe/Edit/${r.id}`, {
      state: { categoryId },
    });
  }}
>
  <EditIcon fontSize="small" />
</IconButton>

        <IconButton
          size="small"
          sx={iconBtnStyle}
          onClick={(e) => {
            e.stopPropagation();
            setRecipeToDelete(r);
            setDeleteDialogOpen(true);
          }}
        >
          <DeleteIcon fontSize="small" />
        </IconButton>
      </Box>
    </Box>
  );
};

/* ================= PAGE ================= */
function CategoryDetail() {
  const { t, i18n } = useTranslation("Recettes");
  const { id } = useParams();
  const navigate = useNavigate();
  const userState = useSelector((state) => state.user);

  const [category, setCategory] = useState(null);
  const [recipes, setRecipes] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [recipeToDelete, setRecipeToDelete] = useState(null);

  const ADMIN_ROLES = ["RecipeAdmin", "AdminTI"];
  //const ADMIN_ROLES = ["ReportAdmin"]; //pour tester ;
  const hasAdminAccess = (roles = []) =>
    roles.some((role) => ADMIN_ROLES.includes(role));
  const canAdmin = hasAdminAccess(userState?.roles || []);
  const handleNoPermission = () => {
    showSnack(t("noPermission"), "warning");
  };

  const showSnack = (message, severity = "info") => {
    setSnack({
      open: true,
      message,
      severity,
    });
  };
  const [snack, setSnack] = useState({
    open: false,
    message: "",
    severity: "success", // success | error | warning | info
  });
  const handleDeleteRecipe = async () => {
    if (!recipeToDelete) return;

    try {
      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/ManageVideoRecipe/DeleteRecipe/${recipeToDelete.id}`,
        {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${userState.token}`,
          },
        },
      );

      const data = await res.json();

      if (!res.ok) {
        console.error(data);
        showSnack(
          userState.lang === "FR" ? data.messageFr : data.messageEn,
          "error",
        );
        return;
      }

      // ✅ update UI sans refresh
      setRecipes((prev) => prev.filter((r) => r.id !== recipeToDelete.id));
    } catch (error) {
      console.error("DELETE ERROR:", error);
    } finally {
      setDeleteDialogOpen(false);
      setRecipeToDelete(null);
    }
  };
  const fetchRecipes = async () => {
    setLoading(true);

    try {
      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/RecipePreparation/GetRecipesByCategory/${id}`,
        {
          headers: {
            Authorization: `Bearer ${userState.token}`,
          },
        },
      );

      const data = await res.json();
      setRecipes(data || []);
    } catch (error) {
      console.error(error);
      setRecipes([]);
    } finally {
      setLoading(false);
    }
  };

  const toggleRecipeActive = async (recipe) => {
    const newStatus = !recipe.estActif;

    const response = await fetch(
      `${process.env.REACT_APP_API_URL}/ManageVideoRecipe/ToggleRecipe/${recipe.id}`,
      {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${userState.token}`,
        },
        body: JSON.stringify({
          estActif: newStatus,
        }),
      },
    );

    const data = await response.json();

    // ❌ IMPORTANT : on STOPPE le flow ici si erreur
    if (!response.ok) {
      throw new Error(
        userState.lang === "FR" ? data.messageFr : data.messageEn,
      );
    }

    // ✅ update UI seulement si OK
    setRecipes((prev) =>
      prev.map((r) => (r.id === recipe.id ? { ...r, estActif: newStatus } : r)),
    );

    // ✅ success message backend
    showSnack(
      userState.lang === "FR" ? data.messageFr : data.messageEn,
      "success",
    );
  };

  useEffect(() => {
    fetchRecipes();
  }, [id]);

  const getName = (r) => (userState.lang === "FR" ? r.nomFr : r.nomEn);

  const filtered = recipes.filter((r) =>
    getName(r)?.toLowerCase().includes(search.toLowerCase()),
  );

  const categoryName = recipes?.[0]
    ? userState.lang === "FR"
      ? recipes[0].categoryTextFr
      : recipes[0].categoryTextEn
    : "Catégorie";

  return (
    <Box sx={{ p: 3, minHeight: "100vh", background: "#f4f6f8" }}>
      {/* BREADCRUMB */}
      <Breadcrumbs sx={{ mb: 2 }}>
        <Link onClick={() => navigate("/")}>Home</Link>
        <Link onClick={() => navigate("/Categoryrecettes")}>Catégories</Link>
        <Typography>{categoryName}</Typography>
      </Breadcrumbs>

      {/* HEADER */}
      <Box
        sx={{
          mb: 3,
          p: 2.5,
          borderRadius: 4,
          background: "white",
          boxShadow: "0 2px 12px rgba(0,0,0,0.06)",
        }}
      >
        {/* TOP */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 2,
            flexWrap: "wrap",
            gap: 2,
          }}
        >
          {/* LEFT */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <IconButton
              onClick={() => navigate("/Categoryrecettes")}
              sx={{
                background: "#f5f5f5",
                "&:hover": {
                  background: "#ececec",
                },
              }}
            >
              <ArrowBackIcon />
            </IconButton>

            <Box>
              <Typography fontSize={22} fontWeight={700}>
                {categoryName}
              </Typography>

              <Typography fontSize={13} color="text.secondary">
                {filtered.length} recettes
              </Typography>
            </Box>
          </Box>

          {/* RIGHT */}
          {/* RIGHT */}
{canAdmin && (
  <Button
    variant="contained"
    startIcon={<AddIcon />}
    onClick={() => navigate(`/Recipe/Add/${id}`)}
    sx={{
      borderRadius: 3,
      textTransform: "none",
      fontWeight: 600,
      px: 2.5,
      height: 40,
    }}
  >
    {userState.lang === "FR"
      ? "Ajouter une recette"
      : "Add new recipe"}
  </Button>
)}
        </Box>

        {/* SEARCH BAR */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 2,
          }}
        >
          <TextField
            fullWidth
            size="small"
            placeholder={
              userState.lang === "FR"
                ? "Rechercher une recette..."
                : "Search recipe..."
            }
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{
              background: "#f8f9fb",
              borderRadius: 2,
            }}
            InputProps={{
              startAdornment: (
                <SearchIcon fontSize="small" sx={{ mr: 1, color: "#999" }} />
              ),
            }}
          />
        </Box>
      </Box>

      {/* GRID */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fill, minmax(340px, 1fr))",
          gap: 2,
        }}
      >
        {loading ? (
          Array.from(new Array(6)).map((_, i) => (
            <Skeleton key={i} variant="rounded" height={100} />
          ))
        ) : filtered.length === 0 ? (
          <Typography>Aucune recette</Typography>
        ) : (
          filtered.map((r) => (
            <RecipeCard
              key={r.id}
              r={r}
              getName={getName}
              navigate={navigate}
              categoryId={id} // 🔥 AJOUT
              setDeleteDialogOpen={setDeleteDialogOpen}
              setRecipeToDelete={setRecipeToDelete}
              toggleRecipeActive={toggleRecipeActive}
              showSnack={showSnack}
              canAdmin={canAdmin}
              userState={userState}
            />
          ))
        )}
      </Box>
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
      >
        <DialogTitle>Confirmation</DialogTitle>

        <DialogContent>
          <DialogContentText>
            Voulez-vous supprimer cette recette ?
          </DialogContentText>
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Annuler</Button>

          <Button color="error" onClick={handleDeleteRecipe}>
            Supprimer
          </Button>
        </DialogActions>
      </Dialog>
      <Snackbar
        open={snack.open}
        autoHideDuration={3000}
        onClose={() => setSnack((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnack((prev) => ({ ...prev, open: false }))}
          severity={snack.severity}
          variant="filled"
        >
          {snack.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default CategoryDetail;
